/**
 * TextOverlayRenderer Component
 *
 * Renders text overlays on top of storyboard images.
 * Overlays use normalized 0-1 coordinates that are converted to absolute
 * pixel positions based on the container size.
 *
 * Features:
 * - Converts normalized coordinates (0-1) to absolute pixels
 * - Supports custom font family, size, color, and alignment
 * - Preset positions for quick placement
 * - Non-interactive (pointerEvents: none) so image interactions work
 */

import React, { CSSProperties } from 'react';
import type { TextOverlay } from '@/types';
import { presetPositionToCoordinates } from '@/utils/overlayPositions';

export interface TextOverlayRendererProps {
  /** Array of text overlays to render */
  textOverlays: TextOverlay[];
  /** Container width in pixels for coordinate conversion */
  containerWidth: number;
  /** Container height in pixels for coordinate conversion */
  containerHeight: number;
}

/**
 * TextOverlayRenderer Component
 *
 * Renders text overlays absolutely positioned on the image container
 */
export function TextOverlayRenderer({
  textOverlays,
  containerWidth,
  containerHeight,
}: TextOverlayRendererProps) {
  const containerStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 15,
  };

  return (
    <div style={containerStyles}>
      {textOverlays.map((overlay) => {
        const opacity = overlay.opacity !== undefined ? overlay.opacity : 1;
        const color = overlay.color || '#FFFFFF';
        const fontFamily = overlay.fontFamily || 'Arial';
        const alignment = overlay.alignment || 'center';
        const fontWeight = overlay.fontWeight || 'normal';
        const fontStyle = overlay.fontStyle || 'normal';
        
        const baseFontSize = overlay.fontSize || 24;
        const containerScaleFactor = containerWidth / 800;
        const scaledFontSize = baseFontSize * containerScaleFactor;

        let posX = overlay.position.x;
        let posY = overlay.position.y;

        if (overlay.presetPosition) {
          const presetCoords = presetPositionToCoordinates(overlay.presetPosition);
          posX = presetCoords.x;
          posY = presetCoords.y;
        }

        const absoluteX = posX * containerWidth;
        const absoluteY = posY * containerHeight;

        let textAlign: CSSProperties['textAlign'] = 'center';
        let transform = 'translate(-50%, -50%)';
        
        if (alignment === 'left') {
          textAlign = 'left';
          transform = 'translate(0, -50%)';
        } else if (alignment === 'right') {
          textAlign = 'right';
          transform = 'translate(-100%, -50%)';
        }

        const overlayContainerStyles: CSSProperties = {
          position: 'absolute',
          left: absoluteX,
          top: absoluteY,
          transform,
          transformOrigin: alignment === 'left' ? 'top left' : alignment === 'right' ? 'top right' : 'top center',
          transition: 'none',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
        };

        const textStyles: CSSProperties = {
          fontSize: `${scaledFontSize}px`,
          fontFamily,
          color,
          textAlign,
          fontWeight,
          fontStyle,
          opacity,
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
          lineHeight: 1.2,
        };

        return (
          <div key={overlay.id} style={overlayContainerStyles} title="Text overlay">
            <span style={textStyles}>{overlay.text}</span>
          </div>
        );
      })}
    </div>
  );
}

export default TextOverlayRenderer;
